import { Message } from 'app/scripts/components/moderation/models/message';
import User from 'app/scripts/models/user';
import { AppealReasons } from '../../../../../constants';
import { RequestStatus } from '../../../../../types';

export type ContentType = 'COMMENTS' | 'POSTS';
export type ActionButton = 'approve' | 'reject';
export type ActionSource = 'mouse' | 'keyboard';

export interface IModerationMessagesState {
  itemsList: Message[];
  lockedItems: Message[];
  isLoading: boolean;
  isSpotItemsFetched: boolean;
  expirationTime: Date | string;
  activeItems: number;
  total: number;
  isModerationMode: boolean;
  pageInfo: IPageInfo;
  counts: MessagesCounts;
  filters: IAdvancedFilters;
  lastFetch: number;
  itemsOnPage: number;
  isMultiSelect: boolean;
  isEmptyResults: boolean;
  spotPolicy: SpotPolicy;
  unpublishedMessagesOffset: number;
  isHighlightTagsEnabled: boolean;
  hasMorePending: boolean;
  hasMorePendingStatus: RequestStatus;
  sessionModeratedMessageCount: number;
}

export interface IPageInfo {
  viewType: ViewType;
  hasNext: boolean;
  offset: number;
  folder: Folder;
  tag: AnyTag[];
  published?: MessagePublishedStatus;
  sortBy: Sorting;
  messageType: MessageType;
  moderatedBy: ModeratedBy;
}

export interface IAdvancedFilters {
  user: string;
  text: string;
  message_id: string;
  conversation: string;
  ip: string;
}

export enum Appearance {
  NORMAL = 'normal',
  COLLAPSED = 'collapsed',
  APPROVED = 'approved',
  BLOCKED = 'blocked'
}

export enum Folder {
  PENDING = 'pending',
  APPROVED = 'approved',
  BLOCKED = 'blocked',
  ALL = 'all'
}

export type MessagesCounts = Record<Folder, number>;

export type AnyTag = 'all' | Tag | TextAnalyzerTag;
export type TextAnalyzerTag = string;

export enum PerspectiveTag {
  GOOGLE_PERSPECTIVE = 'google_perspective',
  ATTACK_ON_AUTHOR = 'attack_on_author',
  ATTACK_ON_COMMENTER = 'attack_on_commenter',
  FLIRTATION = 'flirtation',
  IDENTITY_ATTACK = 'identity_attack',
  INCOHERENT = 'incoherent',
  INFLAMMATORY = 'inflammatory',
  INSULT = 'insult',
  LIKELY_TO_REJECT = 'likely_to_reject',
  OBSCENE = 'obscene',
  PROFANITY = 'profanity',
  SEVERE_TOXICITY = 'severe_toxicity',
  SEXUALLY_EXPLICIT = 'sexually_explicit',
  THREAT = 'threat',
  TOXICITY = 'toxicity',
  UNSUBSTANTIAL = 'unsubstantial',
  SPAM = 'spam'
}

export enum Tag {
  APPEAL = 'appeal',
  APPEAL_REPORTER = 'report-appeal',
  STRICT_ARTICLE = 'strict_article',
  PORN_LINK = 'porn',
  BLACKLISTED_IP = 'blacklisted_ip',
  REPORTED = 'reported',
  EXTERNAL_LINK = 'external_link',
  IMAGE = 'image',
  SPAM = 'spam',
  MALICIOUS = 'malicious',
  RESTRICTED_WORDS = 'restricted_words',
  REJECTED_WORDS = 'rejected_words',
  PUBLISH_AND_MODERATE_WORDS = 'publish_and_moderate_words',
  BLACKLISTED_USER = 'blacklisted_sender',
  RESTRICTED_LANGUAGE = 'foreign_language',
  VIDEO = 'video',
  DUPLICATION = 'duplication',
  MANUAL = 'manual',
  ZALGO = 'zalgo',
  UPPERCASE = 'uppercase',
  INVALID_CHARACTERS = 'invalid_characters',
  OPENWEB_INCIVILITY = 'openweb_incivility',
  OPENWEB_HEBREW_TOXICITY = 'ow_hebrew_toxicity',
  OPENWEB_RACISM = 'ow_racism',
  OPENWEB_SPAM_SIMILARITY = 'ow_spam_similarity',
  ANIMATION = 'animation',
  NON_UNICODE = 'non_unicode',
  // TODO: Merge them to one tag with severity provided in tags metadata.
  USER_INCIVILITY_MEDIUM = 'user_incivility_medium',
  USER_INCIVILITY_HIGH = 'user_incivility_high',
  EMOJIS = 'emojis',
  STAFF_REPORT = 'super_report',
  AKISMET = 'akismet',
  OPENWEB_OVERTURN_MODEL = 'openweb_overturn_model',
  FEATURED = 'featured',
  SUSPECTED_BOT = 'suspected_bot',
  AI_MODERATED = 'ai_moderated',
  HIDDEN_CHARACTERS = 'hidden_characters',
  DOXXING = 'doxxing',
  SUSPENDED = 'suspended',
}

export enum ViewType {
  // For backward support
  USER_HISTORY = 'user-history',
  IP_HISTORY = 'ip-history',
  SPOT_HISTORY = 'spot-history',

  SPOT = 'spot-history',
  TEXT = 'text',
  USER = 'user-history',
  IP = 'ip-history',
  MESSAGE_ID = 'message_id',
  CONVERSATION = 'conversation'
}

export enum Sorting {
  NEWEST = 'newest',
  OLDEST = 'oldest',
  RELEVANCE = 'relevance',
  SAFEST = 'safest'
}

export enum ModerationSearchType {
  USERNAME = 'username',
  EMAIL = 'email',
  MESSAGE_ID = 'Message ID',
  TEXT = 'text'
}

export enum MessageStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  BLOCKED = 'blocked',
  DELETED = 'deleted',
  ARCHIVED = 'archived'
}

export enum MessageType {
  ALL = 'all',
  COMMENT = 'comment',
  REPLY = 'reply',
  POST = 'post'
}

export enum ModeratedBy {
  ALL = 'all',
  ME = 'me'
}

export enum MessagePublishedStatus {
  ALL = 'all',
  PUBILSHED = 'true',
  NOT_PUBILSHED = 'false'
}

export enum UserRole {
  GUEST,
  REGISTERED,
  TEAM_MEMBER,
  COMMUNITY_MODERATOR,
  JOURNALIST,
  MODERATION_VIEWER,
  MODERATOR,
  ADMIN,
  SUPER_ADMIN
}

export enum AuditLogMessageAction {
  CREATED = 'created',
  EDITED = 'edited',
  DELETED = 'deleted',
  PENDING = 'pending',
  APPROVED = 'approved',
  BULK_APPROVED = 'bulk-update-approve',
  REJECTED = 'rejected',
  BULK_REJECTED = 'bulk-update-reject',
  MODERATED = 'moderated',
  REPORTED = 'reported',
  ARCHIVED = 'archived',
  BULK_ARCHIVED = 'bulk-update-archive'
}

export interface AuditLogEvent {
  userId: string;
  currentState: Folder;
  previousState: Folder;
  action: AuditLogMessageAction;
  published: boolean;
  timestamp: number;
}

export interface AuditLog {
  tooltip?: AuditLogEvent | {};
  timeline?: AuditLogEvent[];
}

export interface IModerationUserState {
  users: Record<string, User>;
}

export interface Post {
  id: string;
  title: string;
  postUrl: string;
  policy: SpotPolicy;
}

export interface IModerationPostsState {
  posts: Record<string, Post>;
}

export interface IModerationAuditLogState {
  auditLogs: Record<string, AuditLog>;
}

export interface IModerationSearchState {
  searchValue: string;
  searchType: ModerationSearchType;
  // TODO: type it
  suggestions: any[];
  error: Error | string;
}

export const HotKeysCodes: Record<string, number> = {
  TAB: 9,
  ARROW_UP: 38,
  ARROW_DOWN: 40,
  ARROW_RIGHT: 39,
  ARROW_LEFT: 37,
  SHIFT: 16,
  SPACE: 32,
  ESC: 27,
  ENTER: 13
};

export type HotKeys =
  | 'NEXT'
  | 'UP'
  | 'DOWN'
  | 'APPROVE'
  | 'REJECT'
  | 'SHOW_CONVERSATION'
  | 'GRAB_UP'
  | 'GRAB_DOWN'
  | 'EXIT'
  | 'SELECT'
  | 'NONE';

export interface IModerationHotKeysState {
  isModerationBarActive: boolean;
  cursor: number;
  isConfirmationModalOpen: boolean;
}

export type HotKeysSettings = IModerationHotKeysState;

// TODO: Move to spot state module when created
export enum SpotPolicy {
  FORCE_APPROVE_ALL,
  APPROVE_ALL,
  PUBLISH_AND_MODERATE,
  REQUIRE_APPROVAL,
  BLOCK_ALL,
  FORCE_BLOCK_ALL
}

export type TagsSpanScores = {
  [tagName in PerspectiveTag]: SpanScore[];
};

export type SpanScore = {
  begin: number;
  end: number;
  score: number;
};

export { Message } from 'app/scripts/components/moderation/models/message';
export { default as User } from 'app/scripts/models/user';

export interface ReportReasonItem {
  user_id: string;
  message_id: number;
  description: {
    user_description: string;
  };
  created_at: string;
  main_reason: string;
  sub_reason: string;
  username: string;
}

export interface UserReportItem {
  username: string;
  description?: string;
  className?: string;
}

interface TextAnalyzer {
  ow_models: Record<string, string>;
}

export interface AppealReporterReason {
  username: string;
  message: string;
}

export interface ReportReasons {
  total_count: number;
  reasons: {
    [key: string]: Array<ReportReasonItem>;
  };
}

export interface AIReason {
  verdict: string;
  evidence: string;
}

export interface TagsMetadata {
  report_reasons_V2?: ReportReasons;
  super_report_reasons_V2?: ReportReasons;
  text_analyzer?: TextAnalyzer;
  appeal?: {
    reason: AppealReasons;
    other?: string;
  };
  report_appeals?: {
    count: number;
    appeals: Array<AppealReporterReason>;
  };
  ai_moderated?: AIReason;
}

export interface GetHasMorePendingResponse {
  total_items: {
    pending: number;
  };
}

export interface GetHasMorePendingRequest {
  spotId: string;
  pageInfo: IPageInfo;
}
